import React, { useState, useEffect, useCallback } from 'react';
import { Users, BarChart2, Tag, LogOut, Search, Plus, Trash2, Gift, ChevronLeft, ChevronRight } from 'lucide-react';

const ADMIN_TOKEN_KEY = 'dr_admin_token';
const BASE = process.env.REACT_APP_BACKEND_URL;

async function adminFetch(method, path, body, token) {
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json', 'X-Admin-Token': token },
  };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${BASE}${path}`, opts);
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Request failed');
  return data;
}

function StatCard({ label, value, color }) {
  return (
    <div style={{
      background: '#111827', border: `1px solid ${color}33`,
      borderRadius: 14, padding: '16px 20px', flex: 1, minWidth: 130,
    }}>
      <p style={{ fontSize: 11, color: '#8A8580', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{label}</p>
      <p style={{ fontSize: 28, fontWeight: 700, color, fontFamily: "'Playfair Display', serif" }}>{value ?? '—'}</p>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken]       = useState(() => sessionStorage.getItem(ADMIN_TOKEN_KEY) || '');
  const [password, setPassword] = useState('');
  const [loginErr, setLoginErr] = useState('');
  const [logging, setLogging]   = useState(false);

  const [tab,   setTab]   = useState('dashboard');
  const [stats, setStats] = useState(null);

  // Users tab
  const [users,       setUsers]       = useState([]);
  const [userPage,    setUserPage]    = useState(1);
  const [userTotal,   setUserTotal]   = useState(0);
  const [userPages,   setUserPages]   = useState(1);
  const [userSearch,  setUserSearch]  = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [grantModal,  setGrantModal]  = useState(null); // {uid, name}
  const [grantDays,   setGrantDays]   = useState(30);
  const [granting,    setGranting]    = useState(false);

  // Promos tab
  const [promos,       setPromos]      = useState([]);
  const [loadingPromos, setLoadingPromos] = useState(false);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [newPromo, setNewPromo] = useState({ code: '', type: 'percent', value: 10, maxUses: '', expiresAt: '' });
  const [creatingPromo, setCreatingPromo] = useState(false);

  const isLoggedIn = !!token;

  const doLogin = async (e) => {
    e.preventDefault();
    setLoginErr('');
    setLogging(true);
    try {
      const data = await fetch(`${BASE}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      }).then(r => r.json());
      if (!data.token) throw new Error(data.detail || 'Login failed');
      sessionStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setLoginErr(err.message || 'Invalid password');
    }
    setLogging(false);
  };

  const fetchStats = useCallback(async () => {
    if (!token) return;
    try { const d = await adminFetch('GET', '/api/admin/stats', null, token); setStats(d); }
    catch { /* silent */ }
  }, [token]);

  const fetchUsers = useCallback(async (page = 1, search = '') => {
    if (!token) return;
    setLoadingUsers(true);
    try {
      const d = await adminFetch('GET', `/api/admin/users?page=${page}&limit=15&search=${encodeURIComponent(search)}`, null, token);
      setUsers(d.users || []);
      setUserTotal(d.total || 0);
      setUserPages(d.pages || 1);
      setUserPage(page);
    } catch { /* silent */ }
    setLoadingUsers(false);
  }, [token]);

  const fetchPromos = useCallback(async () => {
    if (!token) return;
    setLoadingPromos(true);
    try { const d = await adminFetch('GET', '/api/admin/promos', null, token); setPromos(d.promos || []); }
    catch { /* silent */ }
    setLoadingPromos(false);
  }, [token]);

  useEffect(() => {
    if (!isLoggedIn) return;
    fetchStats();
  }, [isLoggedIn, fetchStats]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (tab === 'users') fetchUsers(1, userSearch);
    if (tab === 'promos') fetchPromos();
  }, [tab, isLoggedIn]); // eslint-disable-line

  // Debounced search
  useEffect(() => {
    if (tab !== 'users') return;
    const t = setTimeout(() => fetchUsers(1, userSearch), 400);
    return () => clearTimeout(t);
  }, [userSearch]); // eslint-disable-line

  const handleGrant = async () => {
    if (!grantModal) return;
    setGranting(true);
    try {
      await adminFetch('PUT', `/api/admin/users/${grantModal.uid}/grant`, { days: parseInt(grantDays) }, token);
      setGrantModal(null);
      fetchUsers(userPage, userSearch);
    } catch (err) {
      alert(err.message);
    }
    setGranting(false);
  };

  const handleCreatePromo = async (e) => {
    e.preventDefault();
    setCreatingPromo(true);
    try {
      await adminFetch('POST', '/api/admin/promos', {
        code: newPromo.code,
        type: newPromo.type,
        value: parseInt(newPromo.value),
        maxUses: newPromo.maxUses ? parseInt(newPromo.maxUses) : null,
        expiresAt: newPromo.expiresAt || null,
      }, token);
      setNewPromo({ code: '', type: 'percent', value: 10, maxUses: '', expiresAt: '' });
      setShowPromoForm(false);
      fetchPromos();
    } catch (err) { alert(err.message); }
    setCreatingPromo(false);
  };

  const handleDeletePromo = async (code) => {
    if (!window.confirm(`Deactivate promo "${code}"?`)) return;
    try {
      await adminFetch('DELETE', `/api/admin/promos/${code}`, null, token);
      fetchPromos();
    } catch (err) { alert(err.message); }
  };

  const logout = () => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    setToken('');
  };

  // ── Login Screen ──
  if (!isLoggedIn) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0A0F1E',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '40px 24px',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 28, color: '#C9A84C', marginBottom: 8 }}>✦</div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: '#F5F0E8', fontWeight: 700 }}>
            Admin Panel
          </h1>
          <p style={{ fontSize: 13, color: '#8A8580', marginTop: 4 }}>DailyRaashi</p>
        </div>
        <form onSubmit={doLogin} style={{ width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <input
            data-testid="admin-password-input"
            type="password"
            className="input-field"
            placeholder="Admin Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
          {loginErr && <p style={{ color: '#C0392B', fontSize: 13 }}>{loginErr}</p>}
          <button data-testid="admin-login-btn" type="submit" className="btn-primary" disabled={logging}>
            {logging ? 'Logging in…' : 'Enter Admin Panel'}
          </button>
        </form>
      </div>
    );
  }

  // ── Admin Dashboard ──
  return (
    <div style={{ minHeight: '100vh', background: '#0A0F1E', paddingBottom: 48 }}>

      {/* Header */}
      <div style={{
        padding: '56px 20px 16px',
        borderBottom: '1px solid rgba(201,168,76,0.1)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <p style={{ fontSize: 11, color: '#8A8580', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>DailyRaashi</p>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: '#F5F0E8', fontWeight: 700 }}>Admin Panel</h1>
        </div>
        <button
          data-testid="admin-logout-btn"
          onClick={logout}
          style={{ background: 'none', border: 'none', color: '#8A8580', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
        >
          <LogOut size={16} /> Logout
        </button>
      </div>

      {/* Tab Bar */}
      <div style={{ display: 'flex', padding: '12px 20px', gap: 8, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        {[
          { key: 'dashboard', icon: BarChart2, label: 'Dashboard' },
          { key: 'users',     icon: Users,     label: 'Users' },
          { key: 'promos',    icon: Tag,        label: 'Promos' },
        ].map(({ key, icon: Icon, label }) => (
          <button
            key={key}
            data-testid={`admin-tab-${key}`}
            onClick={() => setTab(key)}
            style={{
              flex: 1, padding: '10px 0', borderRadius: 10,
              border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              background: tab === key ? 'rgba(201,168,76,0.15)' : 'transparent',
              color: tab === key ? '#C9A84C' : '#8A8580',
              transition: 'all 0.2s',
            }}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      <div style={{ padding: '20px' }}>

        {/* ── Dashboard ── */}
        {tab === 'dashboard' && (
          <div className="fade-in-up">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
              <StatCard label="Total Users"  value={stats?.totalUsers}   color="#C9A84C" />
              <StatCard label="Trial Active" value={stats?.trialActive}  color="#2D6A4F" />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <StatCard label="Subscribed"   value={stats?.subscribed}   color="#4A90D9" />
              <StatCard label="Readings"     value={stats?.totalReadings} color="#9B59B6" />
            </div>
            <div style={{ marginTop: 24 }}>
              <p style={{ fontSize: 12, color: '#8A8580', textAlign: 'center' }}>
                Promo Codes Active: <strong style={{ color: '#C9A84C' }}>{stats?.totalPromos ?? '—'}</strong>
              </p>
            </div>
          </div>
        )}

        {/* ── Users ── */}
        {tab === 'users' && (
          <div className="fade-in-up">
            {/* Search */}
            <div style={{ position: 'relative', marginBottom: 16 }}>
              <Search size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#8A8580' }} />
              <input
                data-testid="admin-user-search"
                type="text"
                className="input-field"
                placeholder="Search users…"
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                style={{ paddingLeft: 42 }}
              />
            </div>

            <p style={{ fontSize: 12, color: '#8A8580', marginBottom: 12 }}>
              {userTotal} users {userSearch && `matching "${userSearch}"`}
            </p>

            {loadingUsers ? (
              <div style={{ textAlign: 'center', padding: 32 }}>
                <p style={{ color: '#8A8580', fontSize: 13 }}>Loading…</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {users.map(u => (
                  <div key={u.uid} style={{
                    background: '#111827', border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 12, padding: '14px 16px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 14, fontWeight: 600, color: '#F5F0E8', marginBottom: 2,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {u.name || '—'}
                        </p>
                        <p style={{ fontSize: 11, color: '#8A8580', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {u.email || u.phone || '—'}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
                            background: u.status === 'subscribed' ? 'rgba(74,144,217,0.15)' :
                              u.status === 'trial' ? 'rgba(45,106,79,0.15)' : 'rgba(192,57,43,0.12)',
                            color: u.status === 'subscribed' ? '#4A90D9' :
                              u.status === 'trial' ? '#2D6A4F' : '#C0392B',
                            border: `1px solid ${u.status === 'subscribed' ? 'rgba(74,144,217,0.3)' :
                              u.status === 'trial' ? 'rgba(45,106,79,0.3)' : 'rgba(192,57,43,0.2)'}`,
                            textTransform: 'uppercase', letterSpacing: '0.06em',
                          }}>
                            {u.status}
                          </span>
                          {u.subscriptionPlan && (
                            <span style={{ fontSize: 10, color: '#8A8580' }}>{u.subscriptionPlan}</span>
                          )}
                        </div>
                      </div>
                      <button
                        data-testid={`grant-btn-${u.uid}`}
                        onClick={() => { setGrantModal({ uid: u.uid, name: u.name || u.email }); setGrantDays(30); }}
                        style={{
                          background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.2)',
                          borderRadius: 8, padding: '6px 10px', cursor: 'pointer',
                          color: '#C9A84C', display: 'flex', alignItems: 'center', gap: 4,
                          fontSize: 11, fontWeight: 600, flexShrink: 0, marginLeft: 12,
                        }}
                      >
                        <Gift size={13} /> Grant
                      </button>
                    </div>
                    <p style={{ fontSize: 10, color: 'rgba(138,133,128,0.6)', marginTop: 6 }}>
                      Created: {u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-IN') : '—'}
                      {u.subscriptionExpiresAt && ` · Sub expires: ${new Date(u.subscriptionExpiresAt).toLocaleDateString('en-IN')}`}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {userPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginTop: 20 }}>
                <button
                  onClick={() => fetchUsers(userPage - 1, userSearch)}
                  disabled={userPage <= 1}
                  style={{ background: 'none', border: 'none', color: userPage <= 1 ? '#8A8580' : '#C9A84C', cursor: userPage <= 1 ? 'default' : 'pointer' }}
                >
                  <ChevronLeft size={20} />
                </button>
                <span style={{ fontSize: 13, color: '#8A8580' }}>Page {userPage} of {userPages}</span>
                <button
                  onClick={() => fetchUsers(userPage + 1, userSearch)}
                  disabled={userPage >= userPages}
                  style={{ background: 'none', border: 'none', color: userPage >= userPages ? '#8A8580' : '#C9A84C', cursor: userPage >= userPages ? 'default' : 'pointer' }}
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Promos ── */}
        {tab === 'promos' && (
          <div className="fade-in-up">
            <button
              data-testid="create-promo-btn"
              onClick={() => setShowPromoForm(v => !v)}
              className={showPromoForm ? 'btn-secondary' : 'btn-primary'}
              style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              <Plus size={16} />
              {showPromoForm ? 'Cancel' : 'Create Promo Code'}
            </button>

            {showPromoForm && (
              <form onSubmit={handleCreatePromo} style={{
                background: '#111827', border: '1px solid rgba(201,168,76,0.2)',
                borderRadius: 14, padding: '20px', marginBottom: 20,
                display: 'flex', flexDirection: 'column', gap: 12,
              }}>
                <input
                  data-testid="promo-code-input"
                  type="text"
                  className="input-field"
                  placeholder="Code (e.g. JYOTI50)"
                  value={newPromo.code}
                  onChange={e => setNewPromo(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  required
                  style={{ textTransform: 'uppercase', letterSpacing: '0.1em' }}
                />
                <div style={{ display: 'flex', gap: 10 }}>
                  <select
                    data-testid="promo-type-select"
                    className="input-field"
                    value={newPromo.type}
                    onChange={e => setNewPromo(p => ({ ...p, type: e.target.value }))}
                    style={{ flex: 1 }}
                  >
                    <option value="percent">% Discount</option>
                    <option value="days">Free Days</option>
                  </select>
                  <input
                    data-testid="promo-value-input"
                    type="number"
                    className="input-field"
                    placeholder={newPromo.type === 'percent' ? '% off' : 'days'}
                    value={newPromo.value}
                    onChange={e => setNewPromo(p => ({ ...p, value: e.target.value }))}
                    min={1} max={newPromo.type === 'percent' ? 90 : 365}
                    required
                    style={{ flex: 1 }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <input
                    type="number"
                    className="input-field"
                    placeholder="Max uses (optional)"
                    value={newPromo.maxUses}
                    onChange={e => setNewPromo(p => ({ ...p, maxUses: e.target.value }))}
                    min={1}
                    style={{ flex: 1 }}
                  />
                  <input
                    type="date"
                    className="input-field"
                    placeholder="Expiry (optional)"
                    value={newPromo.expiresAt}
                    onChange={e => setNewPromo(p => ({ ...p, expiresAt: e.target.value }))}
                    style={{ flex: 1 }}
                  />
                </div>
                <button data-testid="promo-submit-btn" type="submit" className="btn-primary" disabled={creatingPromo}>
                  {creatingPromo ? 'Creating…' : 'Create Code'}
                </button>
              </form>
            )}

            {loadingPromos ? (
              <p style={{ color: '#8A8580', fontSize: 13, textAlign: 'center', padding: 24 }}>Loading…</p>
            ) : promos.length === 0 ? (
              <p style={{ color: '#8A8580', fontSize: 13, textAlign: 'center', padding: 24 }}>No promo codes yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {promos.map(p => (
                  <div key={p.code} style={{
                    background: '#111827', border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 12, padding: '14px 16px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <div>
                      <p style={{ fontSize: 15, fontWeight: 700, color: '#C9A84C', letterSpacing: '0.08em' }}>{p.code}</p>
                      <p style={{ fontSize: 12, color: '#8A8580', marginTop: 3 }}>
                        {p.type === 'percent' ? `${p.value}% off` : `${p.value} free days`}
                        {p.maxUses && ` · ${p.usedCount || 0}/${p.maxUses} used`}
                        {!p.maxUses && p.usedCount > 0 && ` · used ${p.usedCount}×`}
                      </p>
                      {p.expiresAt && (
                        <p style={{ fontSize: 11, color: '#8A8580', marginTop: 2 }}>
                          Expires: {new Date(p.expiresAt).toLocaleDateString('en-IN')}
                        </p>
                      )}
                    </div>
                    <button
                      data-testid={`delete-promo-${p.code}`}
                      onClick={() => handleDeletePromo(p.code)}
                      style={{
                        background: 'rgba(192,57,43,0.1)', border: '1px solid rgba(192,57,43,0.2)',
                        borderRadius: 8, padding: '6px 10px', cursor: 'pointer',
                        color: '#C0392B', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
                      }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>

      {/* ── Grant Modal ── */}
      {grantModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 300,
          background: 'rgba(8,12,26,0.92)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}
          onClick={e => e.target === e.currentTarget && setGrantModal(null)}
        >
          <div className="fade-in-up" style={{
            background: '#111827', border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: '20px 20px 0 0', padding: '32px 24px 40px',
            width: '100%', maxWidth: 430,
          }}>
            <div style={{ width: 40, height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 2, margin: '0 auto 24px' }} />
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: '#F5F0E8', marginBottom: 6 }}>Grant Subscription</h3>
            <p style={{ fontSize: 13, color: '#8A8580', marginBottom: 20 }}>To: <strong style={{ color: '#F5F0E8' }}>{grantModal.name}</strong></p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20 }}>
              <input
                data-testid="grant-days-input"
                type="number"
                className="input-field"
                value={grantDays}
                onChange={e => setGrantDays(e.target.value)}
                min={1} max={3650}
                style={{ flex: 1 }}
              />
              <span style={{ color: '#8A8580', fontSize: 14 }}>days</span>
            </div>
            <button data-testid="confirm-grant-btn" className="btn-primary" onClick={handleGrant} disabled={granting} style={{ marginBottom: 10 }}>
              {granting ? 'Granting…' : 'Grant'}
            </button>
            <button className="btn-secondary" onClick={() => setGrantModal(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
